'use client';

/**
 * use-voice-recorder — 2026-06-26 (R41 Stage 3, batch ASR rewrite).
 *
 * Replaces streaming-Doubao-over-WS with the proven batch ASR path:
 *   1. MediaRecorder captures user audio into webm/opus locally.
 *   2. On stop, we POST the blob to /api/asr/transcribe (which
 *      forwards to hermes /v1/asr → Doubao bigmodel one-shot).
 *   3. The returned text is appended (or replaces) the draft.
 *
 * Why batch — streaming had ~25 versions of race conditions
 * (mid-utterance close, asr_started ack timing, multiple WS sessions,
 * silence-auto-commit truncation). Batch is a single HTTP round-trip
 * after the user finishes speaking; either it works or it returns a
 * clean error. No state machine wedge possible.
 *
 * Cost: no live transcript feedback while speaking. The user sees
 * a recording indicator + elapsed seconds, then a 1-2s upload+ASR
 * wait, then the editable draft text in a review card.
 *
 * Per Codex R41 spec, final submission to AI is ALWAYS explicit —
 * this hook never auto-submits. It exposes a draft text + actions:
 *   - stopRecording(): blob → upload → draft text returned
 *   - reset(): drop draft, back to idle
 * The caller (session-panel) owns the review card UI + submit click.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export type VoiceRecorderPhase =
  | 'idle' // no recording, no pending draft
  | 'requesting-mic' // calling getUserMedia
  | 'recording' // actively capturing
  | 'transcribing' // uploaded, waiting for ASR result
  | 'error'; // last action failed; check error message

export type VoiceRecorderResult = {
  phase: VoiceRecorderPhase;
  /** seconds elapsed in current recording (live counter while recording). */
  elapsedSec: number;
  /** Last error message, cleared on next start. */
  error: string | null;
  /** Start a fresh recording. Throws on permission failure (also sets phase=error). */
  startRecording: () => Promise<void>;
  /**
   * Stop recording + upload to ASR. Returns the transcript text
   * (possibly empty if ASR returned nothing). Sets phase back to
   * 'idle' on success.
   */
  stopAndTranscribe: () => Promise<string>;
  /**
   * Tear down without uploading (user clicked 重录). Phase → idle.
   */
  cancel: () => void;
};

/**
 * Pick the most widely supported MediaRecorder mime type. Doubao bigmodel
 * accepts opus / webm / wav / m4a; webm/opus is the smallest + universal
 * in modern Chrome/Edge/Safari/Firefox.
 */
function pickMimeType(): string {
  if (typeof MediaRecorder === 'undefined') return 'audio/webm';
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];
  for (const c of candidates) {
    try {
      if (MediaRecorder.isTypeSupported(c)) return c;
    } catch {
      // ignore unsupported queries
    }
  }
  return 'audio/webm';
}

export function useVoiceRecorder(): VoiceRecorderResult {
  const [phase, setPhase] = useState<VoiceRecorderPhase>('idle');
  const [elapsedSec, setElapsedSec] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);

  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef<number>(0);
  const tickerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearTicker = useCallback(() => {
    if (tickerRef.current) {
      clearInterval(tickerRef.current);
      tickerRef.current = null;
    }
  }, []);

  const teardown = useCallback(() => {
    clearTicker();
    try {
      if (recorderRef.current && recorderRef.current.state !== 'inactive') {
        recorderRef.current.stop();
      }
    } catch {
      // best-effort
    }
    recorderRef.current = null;
    if (streamRef.current) {
      for (const track of streamRef.current.getTracks()) {
        try {
          track.stop();
        } catch {
          // ignore
        }
      }
      streamRef.current = null;
    }
    chunksRef.current = [];
    setElapsedSec(0);
  }, [clearTicker]);

  // Tear down on unmount so the mic indicator goes away.
  useEffect(() => {
    return () => {
      teardown();
    };
  }, [teardown]);

  const startRecording = useCallback(async () => {
    if (phase === 'recording' || phase === 'requesting-mic' || phase === 'transcribing') {
      console.warn('[voice-recorder] startRecording ignored — already in', phase);
      return;
    }
    setError(null);
    setPhase('requesting-mic');
    console.warn('[voice-recorder] requesting mic');
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'mic permission denied';
      console.error('[voice-recorder] getUserMedia failed', e);
      // 2026-07-02 (Leon 反馈) — surface a global mic-permission modal so
      // Android Chrome users get step-by-step instructions instead of a
      // silent error state under the mic button.
      try {
        const { reportMicError } = await import('./mic-permission');
        reportMicError(e);
      } catch {
        /* module missing in stripped bundle — silent */
      }
      setError(msg);
      setPhase('error');
      throw e;
    }
    const mimeType = pickMimeType();
    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(stream, { mimeType });
    } catch (e) {
      console.error('[voice-recorder] MediaRecorder ctor failed', e);
      for (const t of stream.getTracks()) t.stop();
      setError(e instanceof Error ? e.message : 'MediaRecorder unavailable');
      setPhase('error');
      throw e;
    }
    chunksRef.current = [];
    recorder.ondataavailable = (ev) => {
      if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data);
    };
    recorder.onerror = (ev) => {
      console.error('[voice-recorder] recorder error', ev);
    };
    streamRef.current = stream;
    recorderRef.current = recorder;
    startedAtRef.current = (typeof performance !== 'undefined' ? performance.now() : 0) || 0;
    setElapsedSec(0);
    // Collect data every 1s so a long pause doesn't lose audio on
    // an unexpected stop (browser memory tab kill, etc).
    recorder.start(1000);
    clearTicker();
    tickerRef.current = setInterval(() => {
      const now = typeof performance !== 'undefined' ? performance.now() : 0;
      setElapsedSec(Math.max(0, Math.floor((now - startedAtRef.current) / 1000)));
    }, 250);
    setPhase('recording');
    console.warn('[voice-recorder] recording started', { mimeType });
  }, [phase, clearTicker]);

  const stopAndTranscribe = useCallback(async (): Promise<string> => {
    if (phase !== 'recording') {
      console.warn('[voice-recorder] stopAndTranscribe called but phase =', phase);
      return '';
    }
    setPhase('transcribing');
    clearTicker();

    // Wait for the recorder's final 'dataavailable' + 'stop' to fire so
    // chunksRef has the trailing slice. MediaRecorder.stop is async by
    // design; we resolve on the 'stop' event.
    const recorder = recorderRef.current;
    if (!recorder) {
      setPhase('idle');
      return '';
    }
    const stopPromise = new Promise<void>((resolve) => {
      recorder.addEventListener('stop', () => resolve(), { once: true });
    });
    try {
      recorder.stop();
    } catch (e) {
      console.error('[voice-recorder] stop() threw', e);
    }
    await stopPromise;

    // Release the mic so the indicator goes away while ASR runs.
    if (streamRef.current) {
      for (const t of streamRef.current.getTracks()) {
        try {
          t.stop();
        } catch {
          // ignore
        }
      }
      streamRef.current = null;
    }
    recorderRef.current = null;

    const blob = new Blob(chunksRef.current, {
      type: chunksRef.current[0]?.type || 'audio/webm',
    });
    chunksRef.current = [];
    if (blob.size < 1000) {
      // Less than 1KB usually means user tapped end immediately. No
      // need to upload — return empty text.
      console.warn('[voice-recorder] tiny blob, skipping upload', { bytes: blob.size });
      setPhase('idle');
      return '';
    }

    console.warn('[voice-recorder] uploading', { bytes: blob.size, type: blob.type });
    const form = new FormData();
    form.append('audio', blob, blob.type.includes('mp4') ? 'recording.mp4' : 'recording.webm');
    form.append('language', 'zh');
    try {
      const r = await fetch('/api/asr/transcribe', {
        method: 'POST',
        credentials: 'include',
        body: form,
      });
      if (!r.ok) {
        const body = await r.text();
        const msg = `转录失败 (${r.status}): ${body.slice(0, 120)}`;
        console.error('[voice-recorder] /api/asr/transcribe failed', { status: r.status, body });
        setError(msg);
        setPhase('error');
        return '';
      }
      const body = (await r.json()) as { text?: string };
      const text = (body.text || '').trim();
      console.warn('[voice-recorder] transcribed', { text_len: text.length });
      setPhase('idle');
      return text;
    } catch (e) {
      const msg = e instanceof Error ? e.message : '网络错误';
      console.error('[voice-recorder] upload threw', e);
      setError(msg);
      setPhase('error');
      return '';
    }
  }, [phase, clearTicker]);

  const cancel = useCallback(() => {
    console.warn('[voice-recorder] cancel — discarding draft', { phase });
    teardown();
    setError(null);
    setPhase('idle');
  }, [phase, teardown]);

  return {
    phase,
    elapsedSec,
    error,
    startRecording,
    stopAndTranscribe,
    cancel,
  };
}
